import { PHOTO } from "./images";

/**
 * Single source of truth for Blue Rock Concrete.
 * Placeholder business facts mirror the Blue Rock family brand — swap the
 * phone/email/founded values for the real concrete-arm details before launch.
 */
export const BUSINESS = {
  name: "Blue Rock Concrete",
  shortName: "Blue Rock",
  tagline: "Stronger from the ground up.",
  /**
   * Real number, confirmed by the client 2026-08-01. The display format here
   * is the canonical one: GBP, Facebook, Yelp and every other citation must
   * match it character for character, or the NAP consistency signal breaks.
   * `phone` and `phoneHref` always change together.
   */
  phone: "(302) 229-8861",
  phoneHref: "tel:+13022298861",
  // ---- PLACEHOLDER — replace before launch ----
  email: "info@bluerockconcreteco.com",
  // ---------------------------------------------
  address: "Wilmington, DE",
  hours: "Mon to Sat, 7am to 7pm",
  founded: 2018,
  yearsExperience: 8,
  rating: 4.9,
  reviews: 60,
  sisterCompany: "Blue Rock Tree Care",
  // Note: the live sister site is bluerocktree.com, not bluerocktreecare.com.
  sisterUrl: "https://www.bluerocktree.com",
  serviceAreas: ["Delaware", "Pennsylvania", "Maryland"],
  social: {
    facebook: "https://www.facebook.com/",
    instagram: "https://www.instagram.com/",
    google: "https://www.google.com/maps",
  },
  /**
   * ⚠️ PLACEHOLDER — the "Leave Us a Review" CTA on the home page points here.
   * The real value is the Google Business Profile review link, which does not
   * exist until the GBP is created and verified:
   *   https://search.google.com/local/writereview?placeid=<PLACE_ID>
   * Swap this the day the profile goes live. Until then the button opens a
   * Google search for the business rather than a broken write-review form.
   */
  reviewUrl: "https://www.google.com/search?q=Blue+Rock+Concrete+Wilmington+DE",
} as const;

/** Site nav — absolute paths so links work from any page. */

/**
 * City markers for the /our-work Delaware map. x/y are viewBox units on the
 * 200x400 stylized state outline in DelawareWorkMap. Only `clickable` cities
 * open a project panel for now; the rest render as dim markers.
 * Projects come from FEATURED_WORK, grouped by its `location` field.
 */
export const WORK_CITIES = [
  { name: "Hockessin", slug: "hockessin", x: 27, y: 18, labelSide: "top", clickable: true },
  { name: "Greenville", slug: "greenville", x: 53, y: 20, labelSide: "top", clickable: true },
  { name: "Newark", slug: "newark", x: 13, y: 48, labelSide: "top", clickable: true },
  { name: "New Castle", slug: "new-castle", x: 58, y: 54, labelSide: "right", clickable: true },
  { name: "Middletown", slug: "middletown", x: 22, y: 114, labelSide: "right", clickable: true },
  { name: "Wilmington", slug: "wilmington", x: 68, y: 32, labelSide: "right", clickable: false },
  { name: "Bear", slug: "bear", x: 38, y: 63, labelSide: "bottom", clickable: false },
  { name: "Glasgow", slug: "glasgow", x: 15, y: 69, labelSide: "top", clickable: false },
  { name: "Dover", slug: "dover", x: 72, y: 198, labelSide: "right", clickable: false },
] as const;

/**
 * NAV_LINKS renders as Home + these items in the centered desktop row
 * (StickyNav), and the Footer renders the same list.
 *
 * "Reviews" was removed on 2026-07-26: the business has no reviews yet, so a
 * whole page of them overstated things. The `/reviews` route still exists but
 * is unlinked and noindexed (see `src/routes/reviews.tsx`) — put this entry
 * back the moment there are real reviews to show. The FAQ that lived on that
 * page moved back onto the home page, so nothing became unreachable.
 */
export const NAV_LINKS = [
  { label: "About", href: "/about" },
  { label: "Services", href: "/services" },
  { label: "Our Work", href: "/our-work" },
  { label: "Process", href: "/process" },
  { label: "Contact", href: "/contact" },
] as const;

/** The 8 core services, ordered by demand + margin (per market research). */
export const SERVICES = [
  {
    title: "Concrete Driveways",
    slug: "concrete-driveways",
    blurb:
      "New installs and full tear-out replacements, built on a proper sub-base to survive Delaware winters without heaving or early cracking.",
    icon: "car",
    img: PHOTO.driveway,
    detailImg: PHOTO.d1,
    overview: [
      "A driveway is the hardest working slab on your property. It carries vehicles every day, sits in full weather year round, and it is the first thing people see when they pull up to your home. We build driveways for that reality: new installs, widenings, and full tear-out replacements across Delaware, Pennsylvania, and Maryland.",
      "Most driveway failures start under the concrete, not in it. That is why we excavate to solid ground, compact a proper stone sub-base, and form to the right thickness for vehicle loads before a single yard of concrete arrives. We pour air-entrained mixes rated for freeze-thaw, cut control joints on a tight pattern, and seal the surface so winter salt and moisture stay out.",
      "If your project connects to a public road, we handle the details, including DelDOT entrance permits when they apply. You get a written line-item quote up front and a written workmanship warranty when we leave.",
    ],
    craft: [
      "Full-depth excavation and a compacted stone sub-base under every pour",
      "Air-entrained, winter-rated mixes with control joints cut to plan",
      "Tear-out and haul-away of your old driveway included in the quote",
      "DelDOT entrance permits handled when your driveway meets the road",
    ],
  },
  {
    title: "Patios & Outdoor Living",
    slug: "concrete-patios",
    blurb:
      "Backyard patios poured for entertaining, from clean broom finishes to decorative upgrades that turn a slab into a destination.",
    icon: "sun",
    img: PHOTO.patio,
    detailImg: PHOTO.d2,
    overview: [
      "A good patio turns the back of your house into the room you use most from April to October. We design and pour patios sized for the way you actually live: grilling, dining, a fire pit, room for the kids to run.",
      "Every patio starts with grading and drainage, so water runs away from your foundation instead of pooling where you sit. From there you choose the finish: a clean broom finish, exposed aggregate, or a stamped and colored surface that ties into the house. We form generous curves and steps where the yard calls for them.",
      "Because it is one monolithic pour, there are no pavers to shift, no joints for weeds, and no yearly re-leveling. Sealed and jointed correctly, a patio we pour will host a lot of summers.",
    ],
    craft: [
      "Graded and pitched so water drains away from your foundation",
      "Broom, exposed aggregate, or stamped finishes to match the house",
      "One monolithic pour, so no shifting pavers or weed-filled joints",
      "Sealed on completion for stain and freeze-thaw resistance",
    ],
  },
  {
    title: "Stamped & Decorative",
    slug: "stamped-decorative-concrete",
    blurb:
      "The look of stone, brick, slate, or wood at a fraction of the cost. Custom patterns, integral color, and sealed for the long haul.",
    icon: "layout-grid",
    img: PHOTO.stamped,
    featured: true,
    detailImg: PHOTO.d3,
    overview: [
      "Stamped concrete gives you the look of natural stone, brick, slate, or wood plank at a fraction of the installed cost of those materials. It is our signature service and the work clients photograph the most.",
      "We combine integral color in the mix, a contrasting release color, and deep-texture stamps to build surfaces with real depth, not a painted-on pattern. Patterns and colors are chosen from physical samples at your kitchen table, so there are no surprises on pour day.",
      "Decorative concrete lives or dies by the sealing schedule. We seal every stamped surface on completion, tell you plainly that it needs resealing every 2 to 4 years, and offer that service when the time comes.",
    ],
    craft: [
      "Integral color plus release color for depth a painted finish cannot match",
      "Deep-texture stamp patterns: stone, slate, brick, and wood plank",
      "Patterns and colors approved from samples before anything is poured",
      "Sealed on completion, with an honest resealing schedule",
    ],
  },
  {
    title: "Walkways & Sidewalks",
    slug: "walkways-sidewalks",
    blurb:
      "Safe, level paths from curb to door, including code-compliant public sidewalk and ADA-ready transitions.",
    icon: "footprints",
    img: PHOTO.walkway,
    detailImg: PHOTO.d4,
    overview: [
      "Walkways carry every guest to your door, and public sidewalks carry your liability. Both need to be level, properly pitched, and built to code. We pour walkways and sidewalks that are safe underfoot in July and in January.",
      "For homes, that means paths wide enough for two people, pitched to shed water, and finished to match the house. For public sidewalk sections, it means meeting New Castle County code, correct panel thickness, and ADA-ready transitions and ramps where they are required.",
    ],
    craft: [
      "Level, trip-safe panels with correct pitch for drainage",
      "Code-compliant public sidewalk work, including ADA transitions",
      "Broom finish for traction in wet and icy weather",
      "Old, heaved paths torn out and hauled away",
    ],
  },
  {
    title: "Steps & Retaining Walls",
    slug: "steps-retaining-walls",
    blurb:
      "Poured steps, stoops, and engineered retaining walls that hold grade, manage water, and look intentional.",
    icon: "layers",
    img: PHOTO.retaining,
    detailImg: PHOTO.d5,
    overview: [
      "Settled steps and bulging walls are more than an eyesore. They are a safety problem and, in the case of a failing retaining wall, a drainage problem working against your whole yard. We pour steps, stoops, and engineered retaining walls that hold their grade for the long term.",
      "Steps get solid footings below frost depth, consistent riser heights that meet code, and a finish that matches the walkway or porch they serve. Retaining walls get engineered footings, steel reinforcement, and drainage behind the wall, because water pressure, not soil, is what pushes walls over.",
    ],
    craft: [
      "Footings poured below frost depth so nothing heaves or settles",
      "Consistent, code-compliant riser heights that are safe underfoot",
      "Steel reinforcement and drainage behind every retaining wall",
      "Finishes matched to the concrete around them",
    ],
  },
  {
    title: "Slabs & Foundations",
    slug: "slabs-foundations",
    blurb:
      "Garage, shed, and equipment pads plus footings and slab-on-grade foundations. Flat, square, and built to spec.",
    icon: "square-stack",
    img: PHOTO.slab,
    detailImg: PHOTO.d6,
    overview: [
      "Garages, sheds, pole barns, additions, and equipment pads all start the same way: a flat, square, correctly cured slab. We pour slab-on-grade foundations and pads built to your plans and to spec.",
      "We shoot grades, form square, compact the base, and place vapor barriers, mesh, or rebar as the job calls for. Anchor bolts and thickened edges go exactly where your builder needs them. When we hand the slab off, it is flat, cured, and ready to build on.",
    ],
    craft: [
      "Laser-checked grades, square forms, and spec thickness throughout",
      "Vapor barrier, mesh, or rebar placed per the plans",
      "Anchor bolts and thickened edges set for your builder",
      "Coordinated with your GC and the building inspector",
    ],
  },
  {
    title: "Repair & Resurfacing",
    slug: "concrete-repair-resurfacing",
    blurb:
      "Crack repair, resurfacing overlays, slab leveling, and sealing that restore worn concrete for a fraction of replacement cost.",
    icon: "wrench",
    img: PHOTO.repair,
    detailImg: PHOTO.d7,
    overview: [
      "Not every worn slab needs a jackhammer. Cracks, surface spalling, settled sections, and tired-looking finishes can often be repaired or resurfaced for a fraction of replacement cost. We tell you honestly which side of that line your concrete is on.",
      "Our repair work includes crack routing and filling, slab leveling for settled sections, and resurfacing overlays that bond a fresh wearing surface over sound existing concrete. Every repair ends with sealing, so the fix lasts instead of reopening after the first winter.",
      "If a slab is too far gone, we will say so and quote the replacement, so you are never paying twice for the same square footage.",
    ],
    craft: [
      "Honest repair-or-replace assessments in writing",
      "Crack routing, filling, and slab leveling for settled sections",
      "Bonded resurfacing overlays that renew the wearing surface",
      "Sealed when finished so repairs survive freeze-thaw",
    ],
  },
  {
    title: "Epoxy Garage Floors",
    slug: "epoxy-garage-floors",
    blurb:
      "High-build epoxy and polyaspartic coatings with flake or metallic finishes. A showroom floor that shrugs off oil, salt, and tires.",
    icon: "sparkles",
    img: PHOTO.epoxy,
    detailImg: PHOTO.d8,
    overview: [
      "An epoxy floor turns a dusty garage slab into a showroom. High-build epoxy and polyaspartic systems shrug off oil, road salt, hot tires, and dropped tools, and they wipe clean with a mop.",
      "Coatings are only as good as the prep under them. We diamond-grind every floor to open the surface, repair cracks and pits, then build the system in layers: primer, base coat, decorative flake or metallic finish, and a clear wear coat. That is why our floors do not peel where others do.",
      "Most garage floors are back in light service within a day or two, and we will give you exact walk-on and drive-on times for the system you choose.",
    ],
    craft: [
      "Diamond grinding, never acid etching, for a bond that lasts",
      "Crack and pit repair before any coating goes down",
      "Flake, metallic, or solid-color systems with a clear wear coat",
      "Clear walk-on and drive-on times for your system",
    ],
  },
] as const;


/**
 * Finish-photo galleries for the /services/$slug pages, keyed by service slug.
 * Excludes each service's own detailImg (shown by ServiceDetailSection on the
 * same page). Cross-page reuse of an id is fine; within one page ids are unique.
 * Thin galleries grow when real client photos land (image-harvester).
 */
export const SERVICE_GALLERIES: Record<(typeof SERVICES)[number]["slug"], readonly string[]> = {
  "concrete-driveways": [PHOTO.driveway, PHOTO.g1, PHOTO.hero],
  "concrete-patios": [PHOTO.patio, PHOTO.g6, PHOTO.showcase],
  "stamped-decorative-concrete": [PHOTO.stamped, PHOTO.g2, PHOTO.g3, PHOTO.g4, PHOTO.showcase],
  "walkways-sidewalks": [PHOTO.walkway, PHOTO.g3],
  "steps-retaining-walls": [PHOTO.retaining],
  "slabs-foundations": [PHOTO.slab, PHOTO.g5, PHOTO.crew],
  "concrete-repair-resurfacing": [PHOTO.repair],
  "epoxy-garage-floors": [PHOTO.epoxy, PHOTO.g5],
};

export const STATS = [
  { value: 8, suffix: "+", label: "Years Pouring Concrete" },
  { value: 4.9, suffix: "★", label: "Average Google Rating" },
  { value: 600, suffix: "+", label: "Projects Completed" },
  { value: 100, suffix: "%", label: "Licensed & Insured" },
] as const;

/** Differentiators competitors in DE mostly miss (from research). */
export const WHY_CHOOSE = [
  {
    icon: "snowflake",
    title: "Built for Delaware Winters",
    description:
      "Air-entrained mixes, correct control joints, and proper sealing so freeze-thaw cycles don't spall or crack your concrete.",
  },
  {
    icon: "shield-check",
    title: "Licensed, Bonded & Insured",
    description:
      "Full New Castle County licensing, bonding, and liability coverage, with proof available before we ever break ground.",
  },
  {
    icon: "badge-check",
    title: "Written Workmanship Warranty",
    description:
      "Every pour is backed by a written warranty in plain English. We stand behind the work long after the trucks leave.",
  },
  {
    icon: "calendar-check",
    title: "Free, Same-Week Estimates",
    description:
      "Fast, honest, no-pressure quotes, usually on-site within the week, with a clear scope and a fair price.",
  },
] as const;

export const PROCESS_STEPS = [
  {
    number: "01",
    title: "Free On-Site Estimate",
    description:
      "We walk the site, talk through your goals, measure, and hand you a clear written quote, with no guesswork and no pressure.",
  },
  {
    number: "02",
    title: "Design & Site Prep",
    description:
      "We finalize finishes and layout, then excavate, grade, form, and compact a proper sub-base, the part most failures trace back to.",
  },
  {
    number: "03",
    title: "Pour & Finish",
    description:
      "Quality mix, precise placement, and the right finish, whether broom, stamped, or decorative, with control joints cut for the long term.",
  },
  {
    number: "04",
    title: "Cleanup & Warranty",
    description:
      "We seal where needed, haul every bit of debris, walk the job with you, and leave you with a written workmanship warranty.",
  },
] as const;

/**
 * The full 7-step build walkthrough for the /process page. The 4-item
 * PROCESS_STEPS above stays as the home flow diagram; this is the craft-level
 * expansion with per-stage photos.
 */
export const PROCESS_DETAIL_STEPS = [
  {
    number: "01",
    title: "Free Estimate",
    points: [
      {
        heading: "We come to you",
        body: "We walk the site together, talk through what you want, and flag anything that affects cost, like drainage, access, or tree roots.",
      },
      {
        heading: "Measured, not guessed",
        body: "We measure the area on the spot and hand you a clear written quote with a defined scope and a fair price. No pressure, no obligation.",
      },
    ],
    img: PHOTO.p1,
    alt: "Contractor measuring a site during a free estimate visit",
  },
  {
    number: "02",
    title: "Design & Planning",
    points: [
      {
        heading: "Pick your finish",
        body: "Broom, stamped, or decorative. We bring pattern and color samples so you can see exactly what your finish will look like.",
      },
      {
        heading: "Plan the details",
        body: "We finalize the layout, plan for water runoff, and lock in a schedule. Where permits apply, we handle them.",
      },
    ],
    img: PHOTO.p2,
    alt: "Project plans and layout drawings on a table",
  },
  {
    number: "03",
    title: "Site Prep & Excavation",
    points: [
      {
        heading: "Tear-out and haul-off",
        body: "Old slab, sod, or debris comes out and gets hauled away. Your property stays protected while we work.",
      },
      {
        heading: "The part you never see",
        body: "We grade the site and compact a proper stone sub-base. Most concrete failures trace back to skipping this step, so we never do.",
      },
    ],
    img: PHOTO.p3,
    alt: "Excavated and graded site with compacted gravel base",
  },
  {
    number: "04",
    title: "Forming & Reinforcement",
    points: [
      {
        heading: "Straight, square, true",
        body: "Forms go in at the exact lines and heights from the plan, so edges finish clean and water runs where it should.",
      },
      {
        heading: "Steel where it counts",
        body: "Rebar or wire mesh gets placed and tied, and every grade gets checked again before we schedule the pour.",
      },
    ],
    img: PHOTO.p4,
    alt: "Wooden forms with tied rebar grid ready for the pour",
  },
  {
    number: "05",
    title: "The Pour",
    points: [
      {
        heading: "The right mix",
        body: "We pour a quality mix rated for Delaware winters, ordered fresh for your job and placed the same day.",
      },
      {
        heading: "Placed with care",
        body: "The crew places, screeds, and levels the slab while the concrete is in its working window, keeping the surface flat and consistent.",
      },
    ],
    img: PHOTO.p5,
    alt: "Crew pouring and screeding fresh concrete",
  },
  {
    number: "06",
    title: "Finishing & Detail Work",
    points: [
      {
        heading: "Your chosen finish",
        body: "Broom texture for grip, stamped patterns for character, or a smooth decorative surface. This is where the craft shows.",
      },
      {
        heading: "Edges and joints",
        body: "Edges get hand-tooled and control joints get cut in the right places, so future movement happens where we planned it, not across your slab.",
      },
    ],
    img: PHOTO.p6,
    alt: "Finisher working the concrete surface by hand",
  },
  {
    number: "07",
    title: "Curing, Sealing & Walkthrough",
    points: [
      {
        heading: "Cure time honored",
        body: "Concrete earns its strength over days, not hours. We tell you exactly when it can take foot traffic and when it can take a vehicle.",
      },
      {
        heading: "Sealed and spotless",
        body: "We apply sealer where the finish calls for it and haul away every scrap of debris before we leave.",
      },
      {
        heading: "Walk it with us",
        body: "We walk the finished job with you and hand over a written workmanship warranty. Done means you are happy with it.",
      },
    ],
    img: PHOTO.p7,
    alt: "Finished sealed concrete driveway at a home",
  },
] as const;

export const FEATURED_WORK = [
  { img: PHOTO.g1, title: "Modern Driveway & Forecourt", tag: "Driveways", location: "Greenville, DE", service: "concrete-driveways" },
  { img: PHOTO.g2, title: "Flagstone Stamped Patio", tag: "Stamped", location: "Hockessin, DE", service: "stamped-decorative-concrete" },
  { img: PHOTO.patio, title: "Covered Patio & Outdoor Living", tag: "Patios", location: "Newark, DE", service: "concrete-patios" },
  { img: PHOTO.g3, title: "Wood-Look Stamped Walkway", tag: "Decorative", location: "Wilmington, DE", service: "stamped-decorative-concrete" },
  { img: PHOTO.g5, title: "Polished Interior Slab", tag: "Slabs", location: "New Castle, DE", service: "slabs-foundations" },
  { img: PHOTO.g4, title: "Slate-Finish Pool Deck", tag: "Stamped", location: "Middletown, DE", service: "stamped-decorative-concrete" },
] as const;

/**
 * About page content. Adapted from the sister company's About page
 * (bluerocktree.com/about), which the client already approved, and retold for
 * the concrete arm.
 *
 * ⚠️ CONFIRM WITH CLIENT before launch: the 2018 founding year comes from
 * `BUSINESS.founded`, and the 2020 tri-state expansion date is inferred from
 * `BUSINESS.serviceAreas`, not from anything the client stated. Nick Coppola's
 * role on the concrete side is written as owner of both companies, which
 * follows from the sister-company relationship but was not confirmed directly.
 */
export const FOUNDER = {
  name: "Nick Coppola",
  role: "Founder & Owner",
  /**
   * No em dashes: see memory/lessons.md 2026-07-11.
   *
   * Deliberately NOT the sister company's founder quote. The one line carried
   * across both brands on purpose is "my name on the truck", which is his own
   * signature phrase. Everything else here is written for concrete, because a
   * near-copy of bluerocktree.com/about would read as filler and duplicate.
   */
  quote:
    "Concrete is not something you redo next season. What we pour is still going to be there in thirty years, long after anyone remembers what it cost. That is why I walk every property myself before we quote it, and why I would rather talk someone out of a job than sell them the wrong one. It is my name on the truck.",
} as const;

export const ABOUT_STORY = [
  "Concrete came second. Nick Coppola started Blue Rock in Wilmington in 2017 doing tree work, and the concrete side grew out of a question he kept getting asked on those job sites: once the stump is gone and the yard is torn up, who do you trust to pour the patio?",
  "He did not love any of the answers he had. So in 2018 he opened Blue Rock Concrete and ran it on the same terms as the tree side. Quote the job honestly, build the base properly, and never pour something you would not want in your own yard. Today that means driveways, patios, walkways, slabs and stamped concrete across Delaware, Pennsylvania and Maryland.",
  "The difference with concrete is that it is permanent. A bad pour does not get better, it gets worse every winter, and it is the homeowner who lives with it. We would rather spend the extra day on excavation and base prep than come back in three years to explain a crack.",
  "We are not a franchise and there is no corporation behind us. We are a local family business, and the owner is still the one who shows up to look at your project.",
] as const;

export const ABOUT_TIMELINE = [
  {
    year: "2017",
    title: "Blue Rock begins",
    detail: "Nick Coppola founds Blue Rock in Wilmington, Delaware.",
  },
  {
    year: "2018",
    title: "Concrete arm opens",
    detail:
      "Blue Rock Concrete launches as the sister company to Blue Rock Tree Care.",
  },
  {
    year: "2020",
    title: "Across the state line",
    detail:
      "Crews start pouring in Pennsylvania and Maryland alongside Delaware.",
  },
  {
    year: "Today",
    title: "Owner on every estimate",
    detail:
      "Licensed, bonded and insured, with a written workmanship warranty on every pour.",
  },
] as const;

/**
 * Written for concrete rather than adapted from the sister company's value
 * list, which used generic virtue headings (Honesty First, Safety Always,
 * Genuine Care). These say something a concrete customer can actually check.
 */
export const ABOUT_VALUES = [
  {
    icon: "badge-check",
    title: "The number does not move",
    description:
      "You get a line item quote before we start, and that is what you pay. No change orders invented halfway through the job.",
  },
  {
    icon: "hard-hat",
    title: "Built for the freeze",
    description:
      "Air entrained mixes, a compacted stone base, and control joints cut on a real plan. Most failures we get called out to fix started under the slab, not on top of it.",
  },
  {
    icon: "home",
    title: "It is a yard, not a job site",
    description:
      "Equipment stays on boards, lawns get protected, and the driveway gets swept before we leave for the night. Not just on the last day.",
  },
  {
    icon: "shield-check",
    title: "Planned before it is poured",
    description:
      "Once concrete leaves the truck the clock is running and there is no pausing to figure something out. So the forms, the access, and the pour order are settled first.",
  },
  {
    icon: "calendar-check",
    title: "On the day we said",
    description:
      "Weather moves a pour and nothing else does. If the forecast turns we call you before you have to call us.",
  },
  {
    icon: "map-pin",
    title: "We live on these roads",
    description:
      "We drive past our own work every week. In a county this size the finished job is the only advertising that matters.",
  },
] as const;

export const TESTIMONIALS = [
  {
    quote:
      "Blue Rock tore out our cracked old driveway and poured a new one that looks incredible. The crew was on time, the site was spotless every night, and the price was exactly what they quoted.",
    name: "Mike R.",
    location: "Wilmington, DE",
    rating: 5,
    service: "Concrete Driveway",
  },
  {
    quote:
      "We went with a stamped concrete patio and could not be happier. They walked us through every pattern and color and the finished work looks like real stone. Highly recommend.",
    name: "Jennifer P.",
    location: "Hockessin, DE",
    rating: 5,
    service: "Stamped Concrete",
  },
  {
    quote:
      "Same family that did our tree work years ago, so we trusted them with the concrete too. Footings and a garage slab, dead level, poured right, and done when they said it would be.",
    name: "Dave & Carol M.",
    location: "Newark, DE",
    rating: 5,
    service: "Slabs & Foundations",
  },
  {
    quote:
      "Our front steps were a hazard. Blue Rock rebuilt them solid and added a matching walkway. Professional, fair, and genuinely nice guys to have around the house.",
    name: "Anthony G.",
    location: "New Castle, DE",
    rating: 5,
    service: "Steps & Walkways",
  },
] as const;

export const FAQ_ITEMS = [
  {
    question: "How much does a concrete driveway cost in Delaware?",
    answer:
      "Most residential driveways land between roughly $5–$18 per square foot installed, plain broom-finish on the lower end, decorative or stamped on the higher end. Tear-out of an old surface, sub-base repair, and site access all factor in. We give free, written, line-item estimates so you know exactly what you're paying for.",
  },
  {
    question: "How long before I can walk or drive on new concrete?",
    answer:
      "You can usually walk on fresh concrete after 24–48 hours. For vehicles, we ask you to wait about 7 days, when concrete reaches roughly 70% of its strength. Full cure is 28 days. We'll give you exact timing for your pour and the weather it's curing in.",
  },
  {
    question: "Stamped concrete or pavers: which should I choose?",
    answer:
      "Stamped concrete is typically less expensive and gives a seamless, custom look with fewer weeds and shifting; it needs resealing every 2–4 years. Pavers cost more but individual units can be swapped if damaged. We install and advise on both and will give you an honest recommendation for your space and budget.",
  },
  {
    question: "When is the best time of year to pour concrete in Delaware?",
    answer:
      "Spring and fall are ideal, since moderate temperatures cure concrete best. We pour year-round, but winter work requires accelerators, heated blankets, and protection from freezing, which adds cost. We'll tell you honestly whether to pour now or schedule for a better window.",
  },
  {
    question: "Do you handle permits?",
    answer:
      "Yes. New Castle County requires a licensed contractor for this work, and a new or modified driveway connecting to a road can require a DelDOT entrance permit. We guide you through what's needed and pull permits where required so the job is done by the book.",
  },
  {
    question: "Are you licensed and insured?",
    answer:
      "Absolutely. Blue Rock Concrete is fully licensed, bonded, and insured, carrying general liability and workers' compensation coverage. We're happy to provide proof before any work begins.",
  },
] as const;

export const TRUST_BADGES = [
  "Licensed, Bonded & Insured",
  "Free Same-Week Estimates",
  "Written Workmanship Warranty",
  "Family-Owned & Operated",
  "Built for Delaware Winters",
] as const;

/** Section-2 "value" band — a statement + 3 pillars (mirrors the reference layout). */
export const VALUE_STATEMENT =
  "Protect your investment with industry-leading craftsmanship and complete concrete services, every pour backed by a written workmanship warranty.";

export const VALUE_PROPS = [
  {
    icon: "hard-hat",
    title: "Premium Concrete Installation",
    description:
      "Our crew pours driveways, patios, and slabs on a proper sub-base built to survive Delaware winters.",
  },
  {
    icon: "wrench",
    title: "Fast & Reliable Repairs",
    description:
      "Cracks, spalling, and settling fixed before they spread or compromise your concrete.",
  },
  {
    icon: "home",
    title: "Stress-Free Replacement",
    description:
      "Old, failing concrete torn out and replaced with a clean, level, long-lasting finish.",
  },
] as const;

/** "What's included" band on /quote — what a free estimate visit covers. */
export const QUOTE_INCLUDES = [
  {
    title: "On-site measurement",
    description:
      "We walk the site with you, measure the space, and flag anything that affects cost, like drainage, access, or grade.",
  },
  {
    title: "A written scope and fixed price",
    description:
      "You get a clear, line-item quote in writing, not a verbal guess. The price we hand you is the price you pay.",
  },
  {
    title: "Mix and finish recommendations",
    description:
      "We tell you honestly which mix, sub-base, and finish fit your project, your budget, and Delaware weather.",
  },
  {
    title: "No deposit and no obligation",
    description:
      "The estimate is free and there's no pressure to book. Take the quote, think it over, and call us when you're ready.",
  },
] as const;

/** "Why it's free" trust band on /quote. No em-dashes. */
export const QUOTE_WHY_FREE =
  "A written, on-site estimate costs us time, not you. We would rather earn the job with an honest number and a clear scope than with pressure tactics. If it's not the right fit, you walk away owing nothing and we won't chase you with follow-up calls.";

/** Reason options for the /contact inquiry form's native select. */
export const CONTACT_TOPICS = [
  "General question",
  "Request a callback",
  "Commercial project",
  "Careers",
  "Something else",
] as const;
